// The "I'm free" pill (spec item 10): a switch inside a pill; when on, a clock icon with how long it lasts, and
// tapping that time offers the lengths (nowish.json freeLengths). Port of web/profile-ui.js renderFreePill.
import { requireAccount } from "../../profile/account";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SHARED, T } from "../../shared/config";
import { Icon } from "../../shared/icons";
import { RULES } from "../../shared/rules";
import { freeLengths, freeRemainingText, freeUntil } from "../../profile/free";
import { useNow } from "../../state/clock";
import { getProfile, useFree } from "../../state/profile";
import styles from "./profile.module.css";

export function FreePill() {
  const free = useFree();
  useNow(); // redraws every tick; the text itself reads the clock, so a length just picked shows exactly ("1h", not "1h 1m")
  const [menu, setMenu] = useState(false);
  const timeBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (!free.on) setMenu(false); }, [free.on]);
  const profile = getProfile();
  return (
    <div id="free-pill" className={`${styles["free-pill"]}${free.on ? ` ${styles["on"]}` : ""}`}>
      <label className={styles["free-switch"]}>
        <span>{T("free.label")}</span>
        <input type="checkbox" role="switch" id="free-toggle" checked={free.on} aria-label={T("free.label")}
          onChange={(ev) => { const on = ev.target.checked; requireAccount("free", () => profile.setFree(on ? SHARED.freeDefaultLength : null)); }} />
        <span className={styles["track"]} aria-hidden="true" />
      </label>
      {free.on && (
        <button ref={timeBtn} type="button" id="free-time" className={styles["free-time"]} aria-haspopup="menu" aria-expanded={menu}
          aria-label={T("free.howLong")} onClick={(ev) => { ev.stopPropagation(); requireAccount("free", () => setMenu((m) => !m)); }}>
          <Icon name="clock" />{freeRemainingText(free, profile.freeLength(), new Date())}
        </button>
      )}
      {menu && <FreeMenu anchor={timeBtn} onClose={() => setMenu(false)} />}
    </div>
  );
}

/** The lengths, above the time button and kept on screen; closes on a tap elsewhere or Escape. */
function FreeMenu({ anchor, onClose }: { anchor: React.RefObject<HTMLButtonElement | null>; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const profile = getProfile();
  const now = new Date();
  const current = profile.freeLength();
  useLayoutEffect(() => {
    const m = box.current, a = anchor.current;
    if (!m || !a) return;
    const r = a.getBoundingClientRect();
    m.style.left = `${Math.min(Math.max(8, r.left), window.innerWidth - m.offsetWidth - 8)}px`;
    m.style.top = `${Math.max(8, r.top - m.offsetHeight - 8)}px`;
  }, [anchor]);
  useEffect(() => {
    const away = (ev: PointerEvent) => { if (!box.current?.contains(ev.target as Node)) onClose(); };
    const key = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); };
    // After this tap, so the tap that opened it doesn't close it.
    const t = setTimeout(() => document.addEventListener("pointerdown", away), 0);
    document.addEventListener("keydown", key);
    return () => { clearTimeout(t); document.removeEventListener("pointerdown", away); document.removeEventListener("keydown", key); };
  }, [onClose]);
  return (
    <div ref={box} id="free-menu" className={styles["menu"]} role="menu">
      {freeLengths().map((l) => {
        const until = freeUntil(l.id, now);
        return (
          <button key={l.id} type="button" role="menuitemradio" aria-checked={current === l.id} data-length={l.id}
            onClick={() => { profile.setFree(l.id); onClose(); }}>
            <span>{l.title}</span>
            {until != null && <span className={styles["muted"]}>{`until ${RULES.timeString(new Date(until))}`}</span>}
            {current === l.id && <span className={styles["check"]}><Icon name="check" /></span>}
          </button>
        );
      })}
    </div>
  );
}
